/* ============================================================
   ProviderPrintJobsCard — 511c826a (c3858b9c part 2, UI half):
   Bambu print actions on the provider order detail. The provider
   picks a bound printer and a completed slicing artifact
   (.gcode.3mf of 16215cbc), sends it straight to the printer over
   LAN, then pauses/resumes/stops the print and refreshes live
   progress (status / gcode_state / percent). All chrome decisions
   live in the pure printJobsView; refresh is manual by design —
   every refresh is a live LAN report read, so nothing polls.
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { api } from '../api/client';
import type { BambuPrintJob } from '../types/printing';
import type { SlicingContext, SlicingJob } from '../types/slicing';
import {
  buildPrintJobRowView,
  buildSendFormView,
  defaultSendPrinter,
  sendArtifactOptions,
} from './printJobsView';

const selectStyle: CSSProperties = {
  padding: '10px 14px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  fontSize: 'var(--fs-body)',
  fontFamily: 'inherit',
  background: 'var(--color-surface)',
  color: 'var(--color-text-primary)',
};

export function ProviderPrintJobsCard({ orderId }: { orderId: string }) {
  const [context, setContext] = useState<SlicingContext | null>(null);
  const [slicingJobs, setSlicingJobs] = useState<SlicingJob[]>([]);
  const [printJobs, setPrintJobs] = useState<BambuPrintJob[]>([]);
  const [printerId, setPrinterId] = useState('');
  const [slicingJobId, setSlicingJobId] = useState('');
  const [busySend, setBusySend] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  /** Job id with an in-flight control/refresh call (row buttons disable). */
  const [busyJobId, setBusyJobId] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const refresh = useCallback(async (id: string) => {
    try {
      const [ctx, slices, prints] = await Promise.all([
        api.getSlicingContext(id),
        api.listSlicingJobs(id),
        api.listOrderPrintJobs(id).catch(() => [] as BambuPrintJob[]),
      ]);
      setContext(ctx);
      setSlicingJobs(slices);
      setPrintJobs(prints);
      setLoadFailed(false);
    } catch {
      // Order not visible to this provider — hide the block entirely.
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    setContext(null);
    setSlicingJobs([]);
    setPrintJobs([]);
    setPrinterId('');
    setSlicingJobId('');
    setSendError(null);
    void refresh(orderId);
  }, [orderId, refresh]);

  // Default picks: first printer with a completed artifact wins.
  useEffect(() => {
    if (!context) return;
    if (!printerId) {
      const pick = defaultSendPrinter(context.printers, slicingJobs);
      if (pick) {
        setPrinterId(pick);
        setSlicingJobId(sendArtifactOptions(slicingJobs, pick)[0]?.slicingJobId ?? '');
      }
      return;
    }
    // Keep the artifact pick valid for the selected printer.
    const options = sendArtifactOptions(slicingJobs, printerId);
    if (!options.some((o) => o.slicingJobId === slicingJobId)) {
      setSlicingJobId(options[0]?.slicingJobId ?? '');
    }
  }, [context, slicingJobs, printerId, slicingJobId]);

  if (loadFailed || !context) return null;

  const artifacts = sendArtifactOptions(slicingJobs, printerId);
  const anyArtifact = context.printers.some(
    (p) => sendArtifactOptions(slicingJobs, p.printerId).length > 0,
  );
  const sendView = buildSendFormView(context.printers, slicingJobs, printerId, slicingJobId);
  // Quiet until there is something to do: a sendable artifact or a job row.
  if (!anyArtifact && printJobs.length === 0) return null;

  const changePrinter = (id: string) => {
    setPrinterId(id);
    setSlicingJobId(sendArtifactOptions(slicingJobs, id)[0]?.slicingJobId ?? '');
    setSendError(null);
  };

  const send = async () => {
    if (!printerId || !slicingJobId) return;
    setBusySend(true);
    setSendError(null);
    try {
      await api.createOrderPrintJob(orderId, printerId, slicingJobId);
      setPrintJobs(await api.listOrderPrintJobs(orderId));
    } catch (e) {
      // Failure reason verbatim from the API guard (unbound printer,
      // wrong-printer file, already printing, LAN unreachable…).
      setSendError(e instanceof Error ? e.message : 'Не удалось отправить на печать');
    } finally {
      setBusySend(false);
    }
  };

  const control = async (jobId: string, command: 'pause' | 'resume' | 'stop') => {
    setBusyJobId(jobId);
    setSendError(null);
    try {
      await api.controlOrderPrintJob(jobId, command);
      setPrintJobs(await api.listOrderPrintJobs(orderId));
    } catch (e) {
      setSendError(e instanceof Error ? e.message : 'Команда не принята');
    } finally {
      setBusyJobId(null);
    }
  };

  const refreshJob = async (jobId: string) => {
    setBusyJobId(jobId);
    setSendError(null);
    try {
      const fresh = await api.refreshOrderPrintJob(jobId);
      setPrintJobs((jobs) => jobs.map((j) => (j.id === fresh.id ? fresh : j)));
    } catch (e) {
      setSendError(e instanceof Error ? e.message : 'Не удалось получить статус принтера');
    } finally {
      setBusyJobId(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', paddingTop: 'var(--space-sm)', borderTop: '1px solid var(--color-separator)' }}>
      <p style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Печать на Bambu
      </p>

      {anyArtifact && (
        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          <select style={{ ...selectStyle, minWidth: 180 }} value={printerId} onChange={(e) => changePrinter(e.target.value)}>
            {context.printers.map((p) => (
              <option key={p.printerId} value={p.printerId}>
                {p.name} · {p.brand} {p.model}
              </option>
            ))}
          </select>
          <select style={{ ...selectStyle, minWidth: 220 }} value={slicingJobId} onChange={(e) => setSlicingJobId(e.target.value)}>
            {artifacts.length === 0 && <option value="">Нет готовых файлов</option>}
            {artifacts.map((a) => (
              <option key={a.slicingJobId} value={a.slicingJobId}>{a.label}</option>
            ))}
          </select>
          <button
            className="btn btn-secondary"
            disabled={busySend || !sendView.canSend}
            onClick={() => void send()}
          >
            {busySend ? '⏳ …' : '🖨 Отправить на печать'}
          </button>
        </div>
      )}

      {sendView.hint && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>{sendView.hint}</p>
      )}

      {sendError && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{sendError}</p>
      )}

      {printJobs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          {printJobs.map((job) => {
            const view = buildPrintJobRowView(job);
            const busy = busyJobId === job.id;
            return (
              <div
                key={job.id}
                style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)', fontSize: 'var(--fs-caption)' }}
              >
                <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className={view.badgeClass}>{view.statusLabel}</span>
                  <span>{job.printerName}</span>
                  {job.artifactFileName && (
                    <span style={{ color: 'var(--color-text-secondary)' }}>{job.artifactFileName}</span>
                  )}
                  {view.progressLabel && (
                    <span style={{ color: 'var(--color-text-secondary)', fontWeight: 'var(--fw-medium)' }}>
                      {view.progressLabel}
                    </span>
                  )}
                  <span style={{ display: 'flex', gap: 'var(--space-xs)', marginLeft: 'auto' }}>
                    {view.canPause && (
                      <button className="btn btn-tertiary" style={{ fontSize: 'var(--fs-caption)' }} disabled={busy} onClick={() => void control(job.id, 'pause')}>
                        ⏸ Пауза
                      </button>
                    )}
                    {view.canResume && (
                      <button className="btn btn-tertiary" style={{ fontSize: 'var(--fs-caption)' }} disabled={busy} onClick={() => void control(job.id, 'resume')}>
                        ▶ Продолжить
                      </button>
                    )}
                    {view.canStop && (
                      <button className="btn btn-tertiary" style={{ fontSize: 'var(--fs-caption)' }} disabled={busy} onClick={() => void control(job.id, 'stop')}>
                        ⏹ Стоп
                      </button>
                    )}
                    {view.canRefresh && (
                      <button className="btn btn-tertiary" style={{ fontSize: 'var(--fs-caption)' }} disabled={busy} onClick={() => void refreshJob(job.id)}>
                        {busy ? '⏳ …' : '↻ Статус'}
                      </button>
                    )}
                  </span>
                </div>
                {/* Live progress bar — thin, flat, light-theme only (DESIGN-SYSTEM). */}
                {view.progressPercent !== null && (
                  <div
                    style={{
                      height: 4,
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--color-bg)',
                      overflow: 'hidden',
                    }}
                    role="progressbar"
                    aria-valuenow={view.progressPercent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      style={{
                        width: `${view.progressPercent}%`,
                        height: '100%',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--color-primary)',
                        transition: 'width 300ms ease',
                      }}
                    />
                  </div>
                )}
                {view.failureText && (
                  <p style={{ margin: 0, fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>
                    {view.failureText}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
